import type { SearchResult } from '@/components/shared/SearchBox';
import type { Destination } from '@/features/destinations/utils/Destination';
import { getDestinationRegionLabel } from '@/features/destinations/utils/DestinationRegionLabelMapper';
import { formatSearchedDestination } from '@/utils/formatSearchedDestination';
import type { RelevanceSearchResult } from '../types/RelevanceSearchResult';

export type DestinationResolution = {
  selectedDestination: Destination;
  searchedDestination: string;
  userSearch?: string;
};

export const UNDECIDED_SEARCH_KEYWORDS = [
  `don't know`,
  `dont know`,
  'not sure',
  'need help',
  `haven't picked`,
  'havent picked',
] as const;

export const UNDECIDED_OPTION_ID = 'undecided';

export const FALLBACK_DESTINATION_NAME = 'Unknown';

export const isSyntheticNonDestinationResult = (
  result: RelevanceSearchResult,
): boolean => {
  if (result.aceDestination !== FALLBACK_DESTINATION_NAME) {
    return false;
  }

  const hasGeoType = Array.isArray(result.type) && result.type.length > 0;
  const hasCountry = Boolean(result.country?.trim());

  return !hasGeoType && !hasCountry;
};

export const findDestinationByName = (
  destinations: Destination[],
  name: string,
): Destination | undefined =>
  destinations.find((destination) => destination.name === name);

export const matchesUndecidedKeywords = (search: string): boolean => {
  const lowerCasedSearch = search.toLowerCase();

  return UNDECIDED_SEARCH_KEYWORDS.some((keyword) =>
    lowerCasedSearch.includes(keyword),
  );
};

export const isUndecidedOnlySearch = (
  searchResults: SearchResult[],
  undecidedOptionId = UNDECIDED_OPTION_ID,
): boolean =>
  searchResults.length === 1 && searchResults[0]?.id === undecidedOptionId;

export const buildDestinationFromRelevanceResult = ({
  relevanceResult,
  destinations,
}: {
  relevanceResult: RelevanceSearchResult;
  destinations: Destination[];
}): DestinationResolution | undefined => {
  const selectedDestination = findDestinationByName(
    destinations,
    relevanceResult.aceDestination,
  );

  if (!selectedDestination) {
    return undefined;
  }

  const regionLabel = getDestinationRegionLabel(selectedDestination.name);

  return {
    selectedDestination: {
      ...selectedDestination,
      name: regionLabel,
    },
    searchedDestination: formatSearchedDestination([
      relevanceResult.name,
      relevanceResult.region,
      relevanceResult.subRegion,
      relevanceResult.country,
    ]),
  };
};

export const buildOtherDestinationResolution = ({
  typedSearch,
  destinations,
  fallbackDestinationName = FALLBACK_DESTINATION_NAME,
}: {
  typedSearch: string;
  destinations: Destination[];
  fallbackDestinationName?: string;
}): DestinationResolution | undefined => {
  const fallbackDestination = findDestinationByName(
    destinations,
    fallbackDestinationName,
  );

  if (!fallbackDestination) {
    return undefined;
  }

  return {
    selectedDestination: fallbackDestination,
    searchedDestination: '',
    userSearch: typedSearch,
  };
};

export type ResolveDestinationInput = {
  typedSearch: string;
  selectedResult?: RelevanceSearchResult;
  relevanceResults: RelevanceSearchResult[];
  searchResults: SearchResult[];
  destinations: Destination[];
  undecidedOptionId?: string;
};

export const resolveDestinationFromSearchInput = ({
  typedSearch,
  selectedResult,
  relevanceResults,
  searchResults,
  destinations,
  undecidedOptionId = UNDECIDED_OPTION_ID,
}: ResolveDestinationInput): DestinationResolution | undefined => {
  const trimmedSearch = typedSearch.trim();

  if (selectedResult) {
    return buildDestinationFromRelevanceResult({
      relevanceResult: selectedResult,
      destinations,
    });
  }

  if (!trimmedSearch) {
    return undefined;
  }

  const firstRelevanceResult = relevanceResults[0];
  const hasDestinationResults =
    searchResults.length > 0 &&
    !isUndecidedOnlySearch(searchResults, undecidedOptionId);

  if (firstRelevanceResult && hasDestinationResults) {
    if (isSyntheticNonDestinationResult(firstRelevanceResult)) {
      const resolution = buildOtherDestinationResolution({
        typedSearch: trimmedSearch,
        destinations,
      });

      if (!resolution) return undefined;

      return {
        ...resolution,
        searchedDestination: formatSearchedDestination([
          firstRelevanceResult.name,
          firstRelevanceResult.region,
          firstRelevanceResult.subRegion,
          firstRelevanceResult.country,
        ]),
      };
    }

    const mappedDestination = buildDestinationFromRelevanceResult({
      relevanceResult: firstRelevanceResult,
      destinations,
    });

    if (mappedDestination) {
      return mappedDestination;
    }
  }

  return buildOtherDestinationResolution({
    typedSearch: trimmedSearch,
    destinations,
  });
};
